import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useFormState } from 'react-hook-form';
import { Box, Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSFileUpload from '../../../shared/components/form-controls/RMSFileUpload/RMSFileUpload';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { PHOTO_DEFAULT_VALUES } from '../../constants/personPhotoFormConstants';
import { uploadPersonPhoto, editPersonImage, createPersonMark } from '../../services/userService';
import PersonPhotoMetaFields from './PersonPhotoMetaFields';

const PersonPhotoDialog = ({
  open,
  onClose,
  personId,
  photo = null,
  hasDefaultPhoto,
  forceMark = false,
  requireFile = true,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const isEditMode = Boolean(photo?.ptsImageID);
  const showFileUpload = !isEditMode && requireFile;

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: isEditMode
      ? {
          ImageTypeCode: photo.ImageTypeCode || '',
          IsMark: Boolean(photo.IsMark),
          IsDefault: Boolean(photo.IsDefault),
          MarkType: photo.MarkType || '',
          Location: photo.Location || '',
          Gang: photo.Gang || '',
          Description: photo.Description || '',
        }
      : { ...PHOTO_DEFAULT_VALUES, IsMark: forceMark },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const file = useMemo(() => pendingFiles[0]?.file ?? null, [pendingFiles]);

  const onSubmit = useCallback(
    async (data) => {
      if (isEditMode) {
        const payload = { ...data, ptsPersonID: personId };

        await executeApiAction({
          setLoading: setSubmitting,
          dispatch,
          action: () => editPersonImage(photo.ptsImageID, payload),
          onSuccess: () => {
            dispatch(notify('Photo updated successfully', 'success'));
            onSaved?.();
          },
        });

        return;
      }

      if (requireFile) {
        const payload = { ...data, ptsPersonID: personId, file };

        await executeApiAction({
          setLoading: setSubmitting,
          dispatch,
          action: () => uploadPersonPhoto(payload),
          onSuccess: () => {
            dispatch(notify('Photo uploaded successfully', 'success'));
            onSaved?.();
          },
        });

        return;
      }

      const payload = { ...data, ptsPersonID: personId };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => createPersonMark(payload),
        onSuccess: () => {
          dispatch(notify('Mark created successfully', 'success'));
          onSaved?.();
        },
      });
    },
    [isEditMode, photo, personId, requireFile, file, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this photo. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Photo' : 'Add Photo'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting || (showFileUpload && !file)}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!photo?.Created}
            created={photo?.Created}
            createdBy={photo?.CreatedBy}
            updated={photo?.Updated}
            updatedBy={photo?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        {showFileUpload && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RMSFileUpload
                files={pendingFiles}
                onChange={setPendingFiles}
                multiple={false}
                maxFiles={1}
                accept="image/*"
                subtitle="Drop an image here or click to browse"
              />
            </Grid>
          </Grid>
        )}

        <Box mt={showFileUpload ? 2 : 0}>
          <PersonPhotoMetaFields
            hasDefaultPhoto={hasDefaultPhoto}
            forceMark={forceMark}
            defaultToMarksType={forceMark && !isEditMode}
          />
        </Box>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonPhotoDialog;
